import { DukeTRPCError } from '@duke-hq/liberror';
import {
  StudentWorkPlacementJourneyStatus,
  StudentEmployerPlacementStatus,
} from '@duke-hq/svc-api-db/client';
import { TERMINAL_EMPLOYER_PLACEMENT_STATUSES } from './employerPlacementStatus';

// Journey statuses that sit in the pre-placement (checklist) phase: the student
// is still completing documents and nominations, no real placement under way.
export const PRE_PLACEMENT_CHECKLIST_JOURNEY_STATUSES: StudentWorkPlacementJourneyStatus[] = [
  StudentWorkPlacementJourneyStatus.AWAITING_CHECKLIST,
  StudentWorkPlacementJourneyStatus.AWAITING_APPROVAL,
];

export const PLACEABLE_JOURNEY_STATUSES = new Set<StudentWorkPlacementJourneyStatus>([
  StudentWorkPlacementJourneyStatus.AWAITING_CONTRACT,
]);

export const TERMINAL_JOURNEY_STATUSES = new Set<StudentWorkPlacementJourneyStatus>([
  StudentWorkPlacementJourneyStatus.COMPLETED,
]);

export function canStartPlacement(status: StudentWorkPlacementJourneyStatus): boolean {
  return PLACEABLE_JOURNEY_STATUSES.has(status);
}

const STUDENT_EDIT_BLOCKED_JOURNEY_STATUSES = new Set<StudentWorkPlacementJourneyStatus>([
  StudentWorkPlacementJourneyStatus.PLACED,
  StudentWorkPlacementJourneyStatus.COMPLETED,
]);

export const STUDENT_VISIBLE_JOURNEY_STATUSES = new Set(
  Object.values(StudentWorkPlacementJourneyStatus).filter(
    (status) => !TERMINAL_JOURNEY_STATUSES.has(status),
  ),
);

export const STUDENT_ACTIONABLE_JOURNEY_STATUSES = new Set(
  [...STUDENT_VISIBLE_JOURNEY_STATUSES].filter(
    (status) => !STUDENT_EDIT_BLOCKED_JOURNEY_STATUSES.has(status),
  ),
);

const IN_FLIGHT_NOMINATION_JOURNEY_STATUSES = new Set<StudentWorkPlacementJourneyStatus>([
  StudentWorkPlacementJourneyStatus.AWAITING_PLACEMENT,
  StudentWorkPlacementJourneyStatus.INTERVIEWING,
  StudentWorkPlacementJourneyStatus.AWAITING_CONTRACT,
]);

const JOURNEY_ON_HOLD_MESSAGE = 'Your work placement is on hold. Please contact your consultant.';

export type NominationEditStage = 'pre_placement' | 'in_flight' | 'blocked';

export type StudentEditableJourney = {
  status: StudentWorkPlacementJourneyStatus;
  onHold: boolean;
};

export function nominationEditStageForJourney(
  journey: StudentEditableJourney,
): NominationEditStage {
  if (journey.onHold || STUDENT_EDIT_BLOCKED_JOURNEY_STATUSES.has(journey.status)) {
    return 'blocked';
  }
  if (IN_FLIGHT_NOMINATION_JOURNEY_STATUSES.has(journey.status)) {
    return 'in_flight';
  }
  return 'pre_placement';
}

export function assertJourneyAcceptsStudentEdits(
  journey: StudentEditableJourney,
  statusBlockedMessage: string,
): void {
  if (journey.onHold) {
    throw new DukeTRPCError({ code: 'BAD_REQUEST', message: JOURNEY_ON_HOLD_MESSAGE });
  }
  if (STUDENT_EDIT_BLOCKED_JOURNEY_STATUSES.has(journey.status)) {
    throw new DukeTRPCError({ code: 'BAD_REQUEST', message: statusBlockedMessage });
  }
}

type JourneyPlacement = {
  status: StudentEmployerPlacementStatus;
  deletedAt: Date | null;
};

export type JourneyUnitCoverage = {
  allRequiredUnitsCovered: boolean;
  allRequiredUnitsComplete: boolean;
};

export function deriveJourneyStatus(
  findingStatus: StudentWorkPlacementJourneyStatus,
  placements: JourneyPlacement[],
  { allRequiredUnitsCovered, allRequiredUnitsComplete }: JourneyUnitCoverage,
): StudentWorkPlacementJourneyStatus {
  const live = placements.filter((p) => p.deletedAt === null);

  if (live.some((p) => !TERMINAL_EMPLOYER_PLACEMENT_STATUSES.has(p.status))) {
    return StudentWorkPlacementJourneyStatus.PLACED;
  }
  if (live.some((p) => p.status === StudentEmployerPlacementStatus.COMPLETED)) {
    return allRequiredUnitsCovered
      ? StudentWorkPlacementJourneyStatus.COMPLETED
      : StudentWorkPlacementJourneyStatus.AWAITING_PLACEMENT;
  }
  if (allRequiredUnitsComplete) {
    return StudentWorkPlacementJourneyStatus.COMPLETED;
  }
  return findingStatus;
}
